import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, API } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import { Search, PlusCircle, X, FileSpreadsheet, FileDown } from 'lucide-react';

const STATUSES = ['Form Not Issued', 'Form Issued', 'Syllabus Sent', 'Test Scheduled', 'Test Completed',
  'Pass', 'Fail', 'Documents Under Review', 'Documents Complete', 'Counselling Pending',
  'Office Recommended', 'Awaiting Rector Approval', 'Returned', 'Approved', 'Rejected',
  'Confirmation Sent', 'Admission Completed'];

const CLASSES = ['Preparatory', 'Lower Nursery', 'Upper Nursery', 'Transition', 'Class I', 'Class II',
  'Class III', 'Class IV', 'Class V', 'Class VI', 'Class VII', 'Class VIII', 'Class IX'];

export default function Applicants() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [exporting, setExporting] = useState('');

  const filters = {
    q: params.get('q') || '',
    session: params.get('session') || '',
    status: params.get('status') || '',
    class_name: params.get('class_name') || '',
    form_issued: params.get('form_issued') || '',
    test_result: params.get('test_result') || '',
  };

  const load = useCallback(async () => {
    setLoading(true);
    const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const r = await api.get('/cases', { params: clean });
    setCases(r.data);
    setLoading(false);
  }, [params]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/settings').then((r) => setSessions(r.data.sessions || [])).catch(() => {}); }, []);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };
  const clearAll = () => setParams(new URLSearchParams());
  const activeCount = Object.values(filters).filter(Boolean).length;

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const r = await api.get(`/cases/export/${fmt}`, { params: clean, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `admissions_report_${stamp}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported (${cases.length} records)`);
    } catch (e) { toast.error('Export failed'); }
    finally { setExporting(''); }
  };

  return (
    <div className="space-y-5" data-testid="applicants-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#172033]">Applicants</h1>
          <p className="text-sm text-[#64748B]">{cases.length} case{cases.length !== 1 ? 's' : ''} found{activeCount > 0 ? ' (filtered)' : ''}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="export-csv-button" disabled={exporting || !cases.length} onClick={() => exportFile('csv')}
            className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#475569] transition hover:border-action hover:text-action disabled:opacity-50">
            <FileSpreadsheet size={16} /> {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
          </button>
          <button data-testid="export-pdf-button" disabled={exporting || !cases.length} onClick={() => exportFile('pdf')}
            className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#475569] transition hover:border-action hover:text-action disabled:opacity-50">
            <FileDown size={16} /> {exporting === 'pdf' ? 'Exporting…' : 'Export Printable PDF'}
          </button>
          <button data-testid="create-case-button" onClick={() => navigate('/applicants/new')}
            className="flex items-center gap-2 rounded-lg bg-action px-4 py-2.5 text-sm font-semibold text-white hover:bg-action-hover">
            <PlusCircle size={17} /> New Admission Case
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              data-testid="applicant-search-input"
              defaultValue={filters.q}
              onChange={(e) => setFilter('q', e.target.value)}
              placeholder="Search name, case ID, phone…"
              className="w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] py-2 pl-9 pr-3 text-sm outline-none focus:border-action focus:bg-white"
            />
          </div>
          <select data-testid="filter-session-select" value={filters.session} onChange={(e) => setFilter('session', e.target.value)}
            className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#475569] outline-none focus:border-action">
            <option value="">All Sessions</option>
            {sessions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-status-select" value={filters.status} onChange={(e) => setFilter('status', e.target.value)}
            className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#475569] outline-none focus:border-action">
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-class-select" value={filters.class_name} onChange={(e) => setFilter('class_name', e.target.value)}
            className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#475569] outline-none focus:border-action">
            <option value="">All Classes</option>
            {CLASSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-form-select" value={filters.form_issued} onChange={(e) => setFilter('form_issued', e.target.value)}
            className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#475569] outline-none focus:border-action">
            <option value="">Form: Any</option>
            <option value="true">Form Issued</option>
            <option value="false">Form Not Issued</option>
          </select>
          {activeCount > 0 && (
            <button data-testid="clear-filters" onClick={clearAll} className="flex items-center gap-1 text-xs font-medium text-[#DC2626] hover:underline">
              <X size={13} /> Clear ({activeCount})
            </button>
          )}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {loading && <p className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-10 text-center text-sm text-[#94A3B8]">Loading…</p>}
        {!loading && cases.length === 0 && <p className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-10 text-center text-sm text-[#94A3B8]">No applicants match these filters.</p>}
        {!loading && cases.map((c) => (
          <button key={c.case_id} data-testid={`applicant-card-${c.case_number}`} onClick={() => navigate(`/cases/${c.case_id}`)}
            className="block w-full rounded-xl border border-[#E2E8F0] bg-white p-4 text-left active:bg-[#F8FAFC]">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold text-[#172033]">{c.applicant_name}</p>
                <p className="font-mono text-xs text-action">{c.case_number}</p>
              </div>
              <StatusBadge status={c.status} size="sm" />
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-[#475569]">
              <span>Class: <b>{c.class_name}</b></span>
              <span>Mobile: {c.contact_number}</span>
              <span className="col-span-2">Officer: {c.assigned_counsellor || '—'}</span>
              <span className="col-span-2 text-[#94A3B8]">Updated {new Date(c.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-xl border border-[#E2E8F0] bg-white md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC] text-left">
                {['Case ID', 'Applicant', 'Class', 'Status', 'Officer', 'Mobile', 'Updated'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#94A3B8]">Loading…</td></tr>}
              {!loading && cases.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#94A3B8]">No applicants match these filters.</td></tr>}
              {!loading && cases.map((c) => (
                <tr key={c.case_id} data-testid={`applicant-row-${c.case_number}`}
                  onClick={() => navigate(`/cases/${c.case_id}`)}
                  className="cursor-pointer border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3 font-mono text-xs font-medium text-action">{c.case_number}</td>
                  <td className="px-4 py-3 font-semibold text-[#172033]">{c.applicant_name}</td>
                  <td className="px-4 py-3 text-[#475569]">{c.class_name}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} size="sm" /></td>
                  <td className="px-4 py-3 text-[#475569]">{c.assigned_counsellor || '—'}</td>
                  <td className="px-4 py-3 text-[#475569]">{c.contact_number}</td>
                  <td className="px-4 py-3 text-xs text-[#94A3B8]">{new Date(c.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
